import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import {
  Radio, LayoutDashboard, Users, Video, BarChart3, Settings,
  Shield, LogOut, Menu, X, ChevronDown, Bell, FileText
} from 'lucide-react';

const navItems = [
  { 
    path: '/admin/home', 
    label: 'Home', 
    icon: LayoutDashboard,
    permission: 'dashboard.view'
  },
  { 
    path: '/admin/streams', 
    label: 'Live Streams', 
    icon: Video,
    permission: 'streams.view'
  },
  { 
    path: '/admin/events', 
    label: 'Events & Posts', 
    icon: FileText,
    permission: 'streams.view'
  },
  { 
    path: '/admin/users', 
    label: 'Users', 
    icon: Users,
    permission: 'users.view'
  },
  { 
    path: '/admin/analytics', 
    label: 'Analytics', 
    icon: BarChart3,
    permission: 'reports.view'
  },
  { 
    path: '/admin/team', 
    label: 'Admin Team', 
    icon: Shield,
    permission: 'admins.view'
  },
  { 
    path: '/admin/settings', 
    label: 'Settings', 
    icon: Settings,
    permission: 'settings.view'
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout, hasPermission, isAuthenticated, loading } = useAdmin();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
          <p className="text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Filter nav items but always show Home for authenticated users
  const filteredNavItems = navItems.filter(item => 
    item.path === '/admin/home' || hasPermission(item.permission)
  );

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="admin-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-zinc-900 border-r border-zinc-800 
        transform transition-transform duration-300 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Radio className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg leading-tight" style={{ fontFamily: 'Manrope' }}>
                NaoView
              </h1>
              <p className="text-indigo-400 text-[10px] font-medium tracking-wider uppercase">Admin Panel</p>
            </div>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {filteredNavItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all
                ${isActive 
                  ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' 
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {admin?.firstName?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {admin?.firstName} {admin?.lastName}
              </p>
              <p className="text-zinc-500 text-xs truncate">{admin?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-16 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800">
          <div className="h-full px-4 lg:px-8 flex items-center justify-between">
            {/* Mobile menu button */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-zinc-400 hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Page title */}
            <h2 className="hidden lg:block text-white font-semibold text-lg" style={{ fontFamily: 'Manrope' }}>
              {filteredNavItems.find(item => location.pathname.startsWith(item.path))?.label || 'Admin'}
            </h2>

            {/* Right section */}
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <button className="relative w-10 h-10 rounded-xl bg-zinc-800/50 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all">
                <Bell className="w-5 h-5" />
                <span className="absolute top-2 right-2 w-2 h-2 bg-indigo-500 rounded-full" />
              </button>

              {/* Profile dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-zinc-800/50 transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                    {admin?.firstName?.charAt(0) || 'A'}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                </button>

                {profileOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setProfileOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-zinc-900 rounded-xl border border-zinc-800 shadow-xl z-50 animate-fadeIn">
                      <div className="p-4 border-b border-zinc-800">
                        <p className="text-white font-medium">{admin?.firstName} {admin?.lastName}</p>
                        <p className="text-zinc-500 text-sm truncate">{admin?.email}</p>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                          data-testid="logout-btn"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}